import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  Skeleton,
  Alert,
  CircularProgress,
  Stack,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Psychology as AiIcon,
  CalendarToday as CalendarIcon,
  AutoAwesome as SparkleIcon,
  TrendingUp as FinancialIcon,
  TrendingUp,
  Business as BusinessIcon,
  Warning as RiskIcon,
  Assessment as AnalysisIcon,
  ShowChart as PerformanceIcon,
  Psychology as ConclusionIcon,
} from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import {
  useStockAnalysisReport,
  useGenerateStockAnalysisReport,
} from "../hooks/useStockAnalysisReport";
import { useReportsList } from "../hooks/useReportsList";
import { useReportQuota } from "../hooks/useReportQuota";
import { GenerationStatusResponse } from "../hooks/useGenerationStatus";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { SERIF_DISPLAY } from "../../../Chat/ChatHero";
import { getSymbolImageUrl } from "../../../../lib/symbolImage";
import { hapticImpact, hapticSelection } from "../../../../lib/haptics";
import ReportPriceChart from "./ReportPriceChart";
import RevenueChart from "./RevenueChart";
import CashFlowBreakdownChart from "./CashFlowBreakdownChart";
import EPSChart from "./EPSChart";
import ExpensesChart from "./ExpensesChart";
import ReportSidebar from "./ReportSidebar";
import QuotaIndicator from "./QuotaIndicator";
import { useTourMode } from "../../../../contexts/TourModeContext";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(advancedFormat);

/**
 * Normalizes text content by converting literal '\n' strings to actual newlines.
 * This handles cases where the AI response contains escaped newlines that weren't
 * properly unescaped during JSON parsing (common in production builds).
 */
const normalizeNewlines = (text: string): string => {
  if (!text) return text;
  // Replace literal \n (backslash + n) with actual newline characters
  return text.replace(/\\n/g, "\n");
};

interface ReportsViewProps {
  ticker: string;
  companyName: string;
}

const StockAnalysisReportSkeleton: React.FC = () => {
  return (
    <Box>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={index % 3 === 0 ? "80%" : "100%"}
              height={20}
              sx={{ mb: 1 }}
            />
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

const ReportSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  content: string;
  color: string;
}> = ({ title, icon, content, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ mb: isMobile ? 3 : 4, textAlign: "center" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: 2,
          pb: 1,
          borderBottom: isMobile
            ? theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid rgba(0, 0, 0, 0.08)"
            : `2px solid ${color}20`,
        }}
      >
        <Box sx={{ color: color, fontSize: isMobile ? "1.125rem" : "1.5rem" }}>
          {icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "1rem" : "1.25rem",
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{
          lineHeight: isMobile ? 1.6 : 1.7,
          fontSize: isMobile ? "0.875rem" : "1rem",
          color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
          whiteSpace: "pre-wrap",
          textAlign: isMobile ? "left" : "center",
          maxWidth: isMobile ? "100%" : 900,
          mx: "auto",
        }}
      >
        {normalizeNewlines(content)}
      </Typography>
    </Box>
  );
};

const ListSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  items: string[];
  color: string;
}> = ({ title, icon, items, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ mb: isMobile ? 3 : 4, textAlign: "center" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: 2,
          pb: 1,
          borderBottom: isMobile
            ? theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid rgba(0, 0, 0, 0.08)"
            : `2px solid ${color}20`,
        }}
      >
        <Box sx={{ color: color, fontSize: isMobile ? "1.125rem" : "1.5rem" }}>
          {icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "1rem" : "1.25rem",
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          maxWidth: isMobile ? "100%" : 900,
          mx: "auto",
          textAlign: isMobile ? "left" : "center",
        }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              mb: 1.5,
              textAlign: isMobile ? "left" : "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                fontSize: isMobile ? "0.875rem" : "1rem",
                color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                display: "inline-flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: color,
                  flexShrink: 0,
                  mt: 0.75,
                }}
              />
              {normalizeNewlines(item)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Generate Report Prompt Component
const GenerateReportPrompt: React.FC<{
  ticker: string;
  onGenerate: () => void;
  isGenerating: boolean;
  quotaExceededMessage?: string | null;
}> = ({ ticker, onGenerate, isGenerating, quotaExceededMessage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data: quota } = useReportQuota();
  const isQuotaExhausted = quota?.remaining === 0;
  // Past the free weekly quota the server still generates while the user can
  // afford the flat credit price — only block when BOTH are gone (mirror of
  // the server's paid path).
  const reportPriceCredits = quota?.credits?.reportPriceCredits ?? 25;
  const extraReportsAvailable = quota?.credits?.extraReportsAvailable ?? 0;
  const canPayWithCredits = isQuotaExhausted && extraReportsAvailable > 0;
  const isBlocked = isQuotaExhausted && !canPayWithCredits;

  // Mobile: a compact, chat-style hero. The desktop card's 64px icon, h5
  // heading and long paragraph pushed the Generate button below the fold on
  // phones — everything here must fit inside the bounded tab without
  // scrolling (the parent scroller still catches any overflow).
  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: 3,
          pt: 3,
          pb: 3,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            backgroundColor: isBlocked
              ? theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.05)"
              : "rgba(34, 197, 94, 0.12)",
          }}
        >
          <AiIcon
            sx={{ fontSize: 18, color: isBlocked ? "#6b7280" : "#22c55e" }}
          />
        </Box>
        <Typography
          component="h2"
          sx={{
            fontFamily: SERIF_DISPLAY,
            fontWeight: 500,
            fontSize: "1.375rem",
            letterSpacing: "-0.01em",
            lineHeight: 1.25,
            color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
            mb: 1,
          }}
        >
          Stock Analysis Report
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.55,
            color: theme.palette.text.secondary,
            maxWidth: 320,
            mb: 3,
          }}
        >
          A comprehensive, hedge fund-style AI report covering financials,
          competitive positioning, risks, business progress and highlights, and
          the overall outlook.
        </Typography>

        {(quotaExceededMessage || isQuotaExhausted) && (
          <Box
            sx={{
              mb: 3,
              width: "100%",
              maxWidth: 400,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              px: 2,
              py: 1.25,
              borderRadius: 1.5,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.07)"
                  : "1px solid rgba(0, 0, 0, 0.07)",
            }}
          >
            <RiskIcon
              sx={{
                color: "#f59e0b",
                fontSize: "1rem",
                mt: 0.25,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.8rem",
                lineHeight: 1.5,
                textAlign: "left",
              }}
            >
              {quotaExceededMessage ??
                (canPayWithCredits
                  ? `You've used your ${quota?.limit ?? 4} free weekly reports — this report will use ${reportPriceCredits} credits from your balance (${extraReportsAvailable} extra ${extraReportsAvailable === 1 ? "report" : "reports"} available).`
                  : `You have reached your weekly limit of ${quota?.limit ?? 4} stock analysis reports. Your quota will reset ${quota?.resetDate ? dayjs(quota.resetDate).format("[on] dddd, MMMM D [at midnight UTC]") : "on Sunday"}.`)}
            </Typography>
          </Box>
        )}

        <Button
          data-tour="generate-report-button"
          onClick={onGenerate}
          disabled={isGenerating || isBlocked}
          variant="contained"
          startIcon={
            isGenerating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SparkleIcon sx={{ fontSize: 18 }} />
            )
          }
          sx={{
            backgroundColor: "#22c55e",
            color: "white",
            fontWeight: 600,
            fontSize: "0.875rem",
            py: 1.25,
            px: 3,
            borderRadius: 2,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#16a34a",
            },
            "&:disabled": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              color:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          {isBlocked && "Weekly Limit Reached"}
          {!isBlocked &&
            canPayWithCredits &&
            `Generate with ${reportPriceCredits} Credits`}
          {!isBlocked && !canPayWithCredits && "Generate Report"}
        </Button>

        {quota && !isQuotaExhausted && (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: "0.72rem",
              fontWeight: 500,
              color: theme.palette.text.disabled,
            }}
          >
            {quota.remaining} of {quota.limit} free weekly reports left
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Card
        sx={{
          width: "100%",
          borderRadius: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.02)",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent sx={{ px: 6, py: 10, textAlign: "center" }}>
          <AiIcon
            sx={{
              fontSize: 64,
              color: isBlocked ? "#6b7280" : "#16a34a",
              mb: 4,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
            }}
          >
            Stock Analysis Report
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 6,
              color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              lineHeight: 1.6,
              maxWidth: 500,
              mx: "auto",
            }}
          >
            A comprehensive, hedge fund-style report covering financials,
            competitive positioning, risks, business progress and highlights,
            and the overall outlook.
          </Typography>

          {/* Quota exceeded notice - subtle inline */}
          {(quotaExceededMessage || isQuotaExhausted) && (
            <Box
              sx={{
                mb: 4,
                maxWidth: 500,
                mx: "auto",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(0, 0, 0, 0.02)",
                border:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.06)"
                    : "1px solid rgba(0, 0, 0, 0.06)",
              }}
            >
              <RiskIcon
                sx={{
                  color: "#f59e0b",
                  fontSize: "1.25rem",
                  mt: 0.1,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.5)"
                      : "rgba(0, 0, 0, 0.45)",
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                  textAlign: "left",
                }}
              >
                {quotaExceededMessage ??
                  (canPayWithCredits
                    ? `You've used your ${quota?.limit ?? 4} free weekly reports — this report will use ${reportPriceCredits} credits from your balance (${extraReportsAvailable} extra ${extraReportsAvailable === 1 ? "report" : "reports"} available).`
                    : `You have reached your weekly limit of ${quota?.limit ?? 4} stock analysis reports. Your quota will reset ${quota?.resetDate ? dayjs(quota.resetDate).format("[on] dddd, MMMM D [at midnight UTC]") : "on Sunday"}.`)}
              </Typography>
            </Box>
          )}

          <Button
            data-tour="generate-report-button"
            onClick={onGenerate}
            disabled={isGenerating || isBlocked}
            variant="contained"
            size="large"
            startIcon={
              isGenerating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SparkleIcon />
              )
            }
            sx={{
              mt: 2,
              backgroundColor:
                isGenerating || isBlocked
                  ? theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                  : "#22c55e",
              color:
                isGenerating || isBlocked
                  ? theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.5)"
                    : "rgba(0, 0, 0, 0.4)"
                  : "white",
              fontWeight: 600,
              py: 2,
              px: 5,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  isGenerating || isBlocked
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)"
                    : "#16a34a",
              },
              "&:disabled": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.5)"
                    : "rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            {isBlocked && "Weekly Limit Reached"}
            {!isBlocked &&
              canPayWithCredits &&
              `Generate with ${reportPriceCredits} Credits`}
            {!isBlocked &&
              !canPayWithCredits &&
              "Generate Stock Analysis Report"}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

// Mobile Report Selector Component - simplified header without background
const MobileReportSelector: React.FC<{
  ticker: string;
  selectedReportId: string | null;
  onSelectReport: (reportId: string) => void;
  onGenerateReport: () => void;
  isGenerating: boolean;
}> = ({
  ticker,
  selectedReportId,
  onSelectReport,
  onGenerateReport,
  isGenerating,
}) => {
  const theme = useTheme();
  const { data: reportsList } = useReportsList(ticker);
  const { data: quota } = useReportQuota();

  const reports = reportsList?.reports ?? [];
  // Past the free weekly quota, credits can still fund extra reports — only
  // block when both are gone (mirror of the server's paid path).
  const isQuotaExhausted = quota?.remaining === 0;
  const canPayWithCredits = (quota?.credits?.extraReportsAvailable ?? 0) > 0;
  const canGenerate = (!isQuotaExhausted || canPayWithCredits) && !isGenerating;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        pb: 1.5,
        width: "100%",
      }}
    >
      {/* Left side: New button */}
      <Button
        onClick={onGenerateReport}
        disabled={!canGenerate}
        variant="contained"
        size="small"
        sx={{
          backgroundColor: canGenerate ? "#22c55e" : "transparent",
          color: canGenerate
            ? "white"
            : theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
          fontWeight: 600,
          py: 0.75,
          px: 2,
          borderRadius: 1,
          textTransform: "none",
          fontSize: "0.8rem",
          minWidth: "auto",
          border: canGenerate
            ? "none"
            : theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          "&:hover": {
            backgroundColor: canGenerate ? "#16a34a" : "transparent",
          },
          "&:disabled": {
            backgroundColor: "transparent",
          },
        }}
      >
        New
      </Button>

      {/* Center: Report dropdown (only if reports exist) */}
      {reports.length > 0 && (
        <FormControl size="small" sx={{ flex: 1, maxWidth: 200 }}>
          <Select
            value={selectedReportId ?? ""}
            onChange={(e) => onSelectReport(e.target.value as string)}
            displayEmpty
            sx={{
              backgroundColor: "transparent",
              borderRadius: 1,
              "& .MuiSelect-select": {
                py: 0.75,
                fontSize: "0.8rem",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            {reports.map((report, index) => (
              <MenuItem key={report.id} value={report.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {dayjs(report.generatedAt).format("MMM D, YYYY")}
                  </Typography>
                  {index === 0 && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        color: "#22c55e",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(34, 197, 94, 0.15)"
                            : "rgba(34, 197, 94, 0.1)",
                        px: 0.5,
                        py: 0.15,
                        borderRadius: 0.5,
                      }}
                    >
                      Latest
                    </Box>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Right side: Quota indicator */}
      <QuotaIndicator compact />
    </Box>
  );
};

// Main Report Content Component
const ReportContent: React.FC<{
  ticker: string;
  companyName: string;
  reportId: string | null;
}> = ({ ticker, companyName, reportId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data, isLoading, error } = useStockAnalysisReport(ticker, {
    reportId,
  });
  const { trackFeature } = useAnalytics();

  // Flagship feature: an AI analysis report was rendered. Keyed on report id so it
  // fires once per distinct report viewed (not on every poll/re-render).
  const viewedReportId = data?.report?.id;
  useEffect(() => {
    if (viewedReportId) {
      trackFeature("ai_report_viewed", { ticker, report_id: viewedReportId });
    }
  }, [viewedReportId, ticker, trackFeature]);

  if (isLoading) {
    return <StockAnalysisReportSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error">
          Failed to load stock analysis report: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!data?.exists || !data.report) {
    return null;
  }

  const report = data.report;

  return (
    <Box>
      {/* Main Report Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: isMobile ? 0 : 3,
          backgroundColor:
            theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
          border: isMobile
            ? "none"
            : theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent sx={{ p: { xs: 0, md: 4 } }}>
          {/* Report Header - Centered */}
          <Box
            sx={{
              mb: isMobile ? 3 : 4,
              textAlign: "center",
              px: isMobile ? 2 : 0,
              pt: isMobile ? 2.5 : 0,
            }}
          >
            {/* Company Logo and Name */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? 1.5 : 2,
                mb: isMobile ? 2 : 3,
              }}
            >
              {/* Company Logo */}
              <Box
                component="img"
                src={getSymbolImageUrl(ticker)}
                alt={`${companyName} logo`}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = "none";
                }}
                sx={{
                  width: isMobile ? 44 : 64,
                  height: isMobile ? 44 : 64,
                  borderRadius: 2,
                  objectFit: "contain",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                  p: 1,
                }}
              />

              {/* Company Name */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                  fontSize: { xs: "1.25rem", md: "2rem" },
                  lineHeight: 1.2,
                }}
              >
                {companyName}
              </Typography>

              {/* Ticker Badge */}
              <Typography
                variant="body2"
                sx={{
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.6)"
                      : "rgba(0, 0, 0, 0.5)",
                  fontSize: isMobile ? "0.72rem" : "0.875rem",
                  fontWeight: isMobile ? 600 : 500,
                  letterSpacing: isMobile ? "0.08em" : "0.5px",
                  ...(isMobile && { textTransform: "uppercase" }),
                }}
              >
                {ticker} · Stock Analysis Report
              </Typography>
            </Box>

            {/* Generation Date with AI Badge */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <CalendarIcon sx={{ fontSize: 16 }} />
                {dayjs(report.generatedAt).format("Do MMM YYYY, h:mm A")}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(34, 197, 94, 0.1)"
                      : "rgba(34, 197, 94, 0.05)",
                  color: "#16a34a",
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 1,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(34, 197, 94, 0.2)"
                      : "1px solid rgba(34, 197, 94, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <AiIcon sx={{ fontSize: 14 }} />
                AI Generated
              </Typography>
            </Box>

            {/* Divider line */}
            <Box
              sx={{
                width: "200px",
                height: "1px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                mx: "auto",
                mt: isMobile ? 2 : 3,
                mb: isMobile ? 0 : 4,
              }}
            />
          </Box>

          {/* Monthly Price Chart */}
          {report.priceData && report.priceData.length > 0 && (
            <ReportPriceChart
              priceData={report.priceData}
              ticker={ticker}
              height={280}
            />
          )}

          {/* Company Overview Content */}
          <Box sx={{ mb: isMobile ? 3 : 5, px: isMobile ? 2 : 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                fontSize: isMobile ? "1rem" : "1.25rem",
                mb: isMobile ? 2 : 3,
                textAlign: "center",
              }}
            >
              Company Overview
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                lineHeight: isMobile ? 1.6 : 1.7,
                color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                whiteSpace: "pre-wrap",
                textAlign: "left",
                width: isMobile ? "100%" : "70%",
                mx: "auto",
              }}
            >
              {normalizeNewlines(report.content.companyOverview)}
            </Typography>
          </Box>

          {/* Financial Analysis - Side by Side */}
          <Box sx={{ mb: isMobile ? 3 : 5, px: isMobile ? 2 : 0 }}>
            {/* Mobile: the group label steps down to the app's uppercase
                micro-label so the four sections beneath carry the weight. */}
            {isMobile && (
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: theme.palette.text.disabled,
                  textAlign: "center",
                  mb: 2,
                }}
              >
                Financial Analysis
              </Typography>
            )}
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 3,
                  pb: 2,
                  borderBottom:
                    theme.palette.mode === "dark"
                      ? "2px solid rgba(255, 255, 255, 0.1)"
                      : "2px solid rgba(0, 0, 0, 0.1)",
                }}
              >
                <FinancialIcon
                  sx={{
                    color: "#3b82f6",
                    fontSize: "1.5rem",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color:
                      theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                    fontSize: "1.25rem",
                  }}
                >
                  Financial Analysis
                </Typography>
              </Box>
            )}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 3, md: 4 }}
              sx={{ mb: { xs: 3, md: 4 } }}
            >
              <Box sx={{ flex: 1 }}>
                {/* Revenue Analysis Section with Chart */}
                <Box sx={{ mb: isMobile ? 0 : 4, textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                      pb: 1,
                      borderBottom: isMobile
                        ? theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.08)"
                          : "1px solid rgba(0, 0, 0, 0.08)"
                        : `2px solid #22c55e20`,
                    }}
                  >
                    <Box
                      sx={{
                        color: "#22c55e",
                        fontSize: isMobile ? "1.125rem" : "1.5rem",
                      }}
                    >
                      <TrendingUp />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        ...(isMobile && { fontSize: "1rem" }),
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      }}
                    >
                      Revenue Analysis
                    </Typography>
                  </Box>

                  {/* Revenue Chart */}
                  <Box sx={{ mb: 3 }}>
                    <RevenueChart
                      ticker={ticker}
                      height={300}
                      noHover={true}
                      renderCard={false}
                      showFullscreen={false}
                    />
                  </Box>

                  {/* Revenue Analysis Text */}
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: isMobile ? 1.6 : 1.7,
                      fontSize: isMobile ? "0.875rem" : "1rem",
                      color:
                        theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                      whiteSpace: "pre-wrap",
                      textAlign: "left",
                      maxWidth: isMobile ? "100%" : 900,
                      mx: "auto",
                      px: isMobile ? 0 : 6,
                    }}
                  >
                    {normalizeNewlines(
                      report.content.financialAnalysis.revenue,
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                {/* Free Cash Flow Section with Chart */}
                <Box sx={{ mb: isMobile ? 0 : 4, textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                      pb: 1,
                      borderBottom: isMobile
                        ? theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.08)"
                          : "1px solid rgba(0, 0, 0, 0.08)"
                        : `2px solid #22c55e20`,
                    }}
                  >
                    <Box
                      sx={{
                        color: "#22c55e",
                        fontSize: isMobile ? "1.125rem" : "1.5rem",
                      }}
                    >
                      <TrendingUp />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        ...(isMobile && { fontSize: "1rem" }),
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      }}
                    >
                      Free Cash Flow
                    </Typography>
                  </Box>

                  {/* Free Cash Flow Chart */}
                  <Box sx={{ mb: 3 }}>
                    <CashFlowBreakdownChart
                      ticker={ticker}
                      height={300}
                      noHover={true}
                      renderCard={false}
                      showFullscreen={false}
                    />
                  </Box>

                  {/* Free Cash Flow Analysis Text */}
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: isMobile ? 1.6 : 1.7,
                      fontSize: isMobile ? "0.875rem" : "1rem",
                      color:
                        theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                      whiteSpace: "pre-wrap",
                      textAlign: "left",
                      maxWidth: isMobile ? "100%" : 900,
                      mx: "auto",
                      px: isMobile ? 0 : 6,
                    }}
                  >
                    {normalizeNewlines(
                      report.content.financialAnalysis.freeCashFlow,
                    )}
                  </Typography>
                </Box>
              </Box>
            </Stack>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 3, md: 4 }}
            >
              <Box sx={{ flex: 1 }}>
                {/* Earnings Per Share Section with Chart */}
                <Box sx={{ mb: isMobile ? 0 : 4, textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                      pb: 1,
                      borderBottom: isMobile
                        ? theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.08)"
                          : "1px solid rgba(0, 0, 0, 0.08)"
                        : `2px solid #22c55e20`,
                    }}
                  >
                    <Box
                      sx={{
                        color: "#22c55e",
                        fontSize: isMobile ? "1.125rem" : "1.5rem",
                      }}
                    >
                      <TrendingUp />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        ...(isMobile && { fontSize: "1rem" }),
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      }}
                    >
                      Earnings Per Share
                    </Typography>
                  </Box>

                  {/* EPS Chart */}
                  <Box sx={{ mb: 3 }}>
                    <EPSChart
                      ticker={ticker}
                      height={300}
                      noHover={true}
                      renderCard={false}
                      showFullscreen={false}
                    />
                  </Box>

                  {/* EPS Analysis Text */}
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: isMobile ? 1.6 : 1.7,
                      fontSize: isMobile ? "0.875rem" : "1rem",
                      color:
                        theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                      whiteSpace: "pre-wrap",
                      textAlign: "left",
                      maxWidth: isMobile ? "100%" : 900,
                      mx: "auto",
                      px: isMobile ? 0 : 6,
                    }}
                  >
                    {normalizeNewlines(report.content.financialAnalysis.eps)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                {/* Expense Management Section with Chart */}
                <Box sx={{ mb: isMobile ? 0 : 4, textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 2,
                      pb: 1,
                      borderBottom: isMobile
                        ? theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.08)"
                          : "1px solid rgba(0, 0, 0, 0.08)"
                        : `2px solid #22c55e20`,
                    }}
                  >
                    <Box
                      sx={{
                        color: "#22c55e",
                        fontSize: isMobile ? "1.125rem" : "1.5rem",
                      }}
                    >
                      <TrendingUp />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        ...(isMobile && { fontSize: "1rem" }),
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      }}
                    >
                      Expense Management
                    </Typography>
                  </Box>

                  {/* Expenses Chart */}
                  <Box sx={{ mb: 1.8 }}>
                    <ExpensesChart
                      ticker={ticker}
                      height={300}
                      noHover={true}
                      renderCard={false}
                      showFullscreen={false}
                    />
                  </Box>

                  {/* Expense Management Analysis Text */}
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: isMobile ? 1.6 : 1.7,
                      fontSize: isMobile ? "0.875rem" : "1rem",
                      color:
                        theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                      whiteSpace: "pre-wrap",
                      textAlign: "left",
                      maxWidth: isMobile ? "100%" : 900,
                      mx: "auto",
                      px: isMobile ? 0 : 6,
                    }}
                  >
                    {normalizeNewlines(
                      report.content.financialAnalysis.expenses,
                    )}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Milestones & Concerns - Side by Side */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 0, md: 4 }}
            sx={{ mb: { xs: 2, md: 4 }, px: isMobile ? 2 : 0 }}
          >
            <Box sx={{ flex: 1 }}>
              <ListSection
                title="Latest Milestones"
                icon={<SparkleIcon />}
                items={report.content.latestMilestones}
                color="#22c55e"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <ListSection
                title="Key Concerns"
                icon={<RiskIcon />}
                items={report.content.keyConcerns}
                color="#ef4444"
              />
            </Box>
          </Stack>

          {/* Competitive Analysis */}
          <Box sx={{ px: isMobile ? 2 : 0 }}>
            <ReportSection
              title="Competitive Differentiation"
              icon={<BusinessIcon />}
              content={report.content.differentiationFactors}
              color="#6366f1"
            />
          </Box>

          {/* Performance & Targets - Side by Side */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 0, md: 4 }}
            sx={{ mb: { xs: 2, md: 4 }, px: isMobile ? 2 : 0 }}
          >
            <Box sx={{ flex: 1 }}>
              <ReportSection
                title="Stock Performance Analysis"
                icon={<PerformanceIcon />}
                content={report.content.performanceAnalysis}
                color="#f59e0b"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <ReportSection
                title="Analyst Grades"
                icon={<AnalysisIcon />}
                content={report.content.analystGrades}
                color="#8b5cf6"
              />
            </Box>
          </Stack>

          {/* Investment Conclusion */}
          <Box
            sx={{
              mt: isMobile ? 3 : 5,
              mb: isMobile ? 3 : 4,
              mx: "auto",
              maxWidth: isMobile ? "100%" : 900,
              textAlign: "center",
              px: isMobile ? 2 : 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: isMobile ? 2 : 3,
                pb: 2,
                borderBottom:
                  theme.palette.mode === "dark"
                    ? "2px solid rgba(255, 255, 255, 0.1)"
                    : "2px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              <ConclusionIcon
                sx={{
                  color: "#6366f1",
                  fontSize: isMobile ? "1.25rem" : "1.5rem",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                  fontSize: isMobile ? "1rem" : "1.25rem",
                }}
              >
                Investment Conclusion
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                lineHeight: isMobile ? 1.6 : 1.8,
                color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                whiteSpace: "pre-wrap",
                textAlign: "left",
                px: isMobile ? 0 : 2,
              }}
            >
              {normalizeNewlines(report.content.conclusion)}
            </Typography>
            <Box
              sx={{
                mt: isMobile ? 2 : 3,
                pt: 2,
                borderTop:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.05)"
                    : "1px solid rgba(0, 0, 0, 0.05)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  fontStyle: "italic",
                  lineHeight: 1.6,
                  textAlign: "center",
                  display: "block",
                  maxWidth: isMobile ? "100%" : 700,
                  mx: "auto",
                }}
              >
                This analysis is AI-generated and should not be considered as
                investment advice. While AI models strive for accuracy, they may
                occasionally produce errors, misinterpret data, or omit critical
                context. Investors should independently verify all information
                and conduct thorough due diligence before making investment
                decisions.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

/**
 * ReportsView - Shows the reports list, content, and generation controls.
 * This component is rendered when there is NO active generation in progress.
 */
const ReportsView: React.FC<ReportsViewProps> = ({ ticker, companyName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isTourMode } = useTourMode();

  // State for selected report
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [quotaExceededMessage, setQuotaExceededMessage] = useState<
    string | null
  >(null);

  // Query client for optimistic updates
  const queryClient = useQueryClient();

  // Fetch reports list
  const { data: reportsList, isLoading: isLoadingList } =
    useReportsList(ticker);

  // Fetch the selected report (or most recent if none selected)
  const { data: reportData, isLoading: isLoadingReport } =
    useStockAnalysisReport(ticker, {
      reportId: selectedReportId,
    });

  // Generate mutation
  const generateMutation = useGenerateStockAnalysisReport(ticker);

  const reports = reportsList?.reports ?? [];
  const hasReports = reports.length > 0;

  // Select the newest report when the list first loads, and again whenever a
  // new one lands at the top. Generation unmounts this view, so the remounted
  // instance first sees the cached list (old newest) and only later the
  // refetched list with the new report; it must follow that change.
  const newestReportId = reports[0]?.id ?? null;
  const previousNewestRef = useRef<string | null>(null);
  useEffect(() => {
    if (!newestReportId) return;
    const previousNewest = previousNewestRef.current;
    previousNewestRef.current = newestReportId;
    const newReportArrived =
      previousNewest !== null && previousNewest !== newestReportId;
    if (!selectedReportId || newReportArrived) {
      setSelectedReportId(newestReportId);
    }
  }, [newestReportId, selectedReportId]);

  // Handle successful generation - select the new report
  useEffect(() => {
    if (generateMutation.data?.success && generateMutation.data?.report) {
      setSelectedReportId(generateMutation.data.report.id);
      setQuotaExceededMessage(null);
    }
  }, [generateMutation.data]);

  // Handle quota exceeded
  useEffect(() => {
    if (generateMutation.data?.quotaExceeded) {
      setQuotaExceededMessage(
        generateMutation.data.message ?? "Weekly report limit reached",
      );
    }
  }, [generateMutation.data?.quotaExceeded, generateMutation.data?.message]);

  // Handle report selection
  const handleSelectReport = (reportId: string) => {
    hapticSelection();
    setSelectedReportId(reportId);
  };

  // Handle generate report
  const handleGenerateReport = () => {
    hapticImpact();
    setQuotaExceededMessage(null);

    // Optimistically set isGenerating to true for immediate UI feedback.
    // This switches to GeneratingView instantly without waiting for server confirmation.
    // If generation fails, the 15s polling will correct the state automatically.
    // IMPORTANT: Query key must include isTourMode to match useGenerationStatus
    queryClient.setQueryData<GenerationStatusResponse>(
      ["stockAnalysisGenerationStatus", ticker, isTourMode],
      (old) => ({
        ticker,
        isGenerating: true,
        timeoutSeconds: old?.timeoutSeconds ?? 300,
      }),
    );

    generateMutation.mutate({});
  };

  // Show loading state
  if (isLoadingList) {
    return <StockAnalysisReportSkeleton />;
  }

  // No reports - show generate prompt
  if (!hasReports) {
    return (
      <Box sx={{ display: "flex", height: "100%" }}>
        {/* Main content - generate prompt. Mobile: nothing below the prompt,
            so no scrolling — a scrollable empty page reads as broken. */}
        <Box sx={{ flex: 1, overflow: isMobile ? "hidden" : "auto" }}>
          {isMobile && (
            <MobileReportSelector
              ticker={ticker}
              selectedReportId={selectedReportId}
              onSelectReport={handleSelectReport}
              onGenerateReport={handleGenerateReport}
              isGenerating={false}
            />
          )}
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pb: { xs: 2, md: 3 },
              // Mobile: the prompt sits in a card like every other block on
              // the ticker page.
              ...(isMobile && {
                "& > *": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                },
              }),
            }}
          >
            <GenerateReportPrompt
              ticker={ticker}
              onGenerate={handleGenerateReport}
              isGenerating={false}
              quotaExceededMessage={quotaExceededMessage}
            />
          </Box>
        </Box>
        {/* Sidebar - right side, empty state (hidden in tour mode) */}
        {!isMobile && !isTourMode && (
          <ReportSidebar
            ticker={ticker}
            selectedReportId={null}
            onSelectReport={handleSelectReport}
            onGenerateReport={handleGenerateReport}
            isGenerating={false}
          />
        )}
      </Box>
    );
  }

  // Has reports - show full layout
  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      {/* Main content area - on left */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Mobile selector (hidden in tour mode) */}
        {isMobile && !isTourMode && (
          <MobileReportSelector
            ticker={ticker}
            selectedReportId={selectedReportId}
            onSelectReport={handleSelectReport}
            onGenerateReport={handleGenerateReport}
            isGenerating={false}
          />
        )}

        {/* Report content or loading state */}
        <Box
          className="selectable-text"
          sx={{ px: { xs: 0, md: 3 }, pb: { xs: 0, md: 3 } }}
        >
          {isLoadingReport && <StockAnalysisReportSkeleton />}
          {!isLoadingReport && reportData?.exists && reportData?.report && (
            <ReportContent
              ticker={ticker}
              companyName={companyName}
              reportId={selectedReportId}
            />
          )}
          {!isLoadingReport && !reportData?.exists && (
            <GenerateReportPrompt
              ticker={ticker}
              onGenerate={handleGenerateReport}
              isGenerating={false}
              quotaExceededMessage={quotaExceededMessage}
            />
          )}
        </Box>
      </Box>

      {/* Sidebar - right side, desktop only (hidden in tour mode) */}
      {!isMobile && !isTourMode && (
        <ReportSidebar
          ticker={ticker}
          selectedReportId={selectedReportId}
          onSelectReport={handleSelectReport}
          onGenerateReport={handleGenerateReport}
          isGenerating={false}
        />
      )}
    </Box>
  );
};

export default ReportsView;
